import type { CellDisplay } from '@shared/cell-format';
import { initialsOf } from '@shared/cell-format';
import type { DataTableCellTone } from '@shared/types/views';
import { ToneDot } from '@/components/views/atoms/ToneDot';
import { cn } from '@/lib/utils';

const TONE_PILL: Record<DataTableCellTone, string> = {
  good: 'bg-[#e8f5ec]',
  warning: 'bg-[#fdf0da]',
  critical: 'bg-[#fdecea]',
  neutral: 'bg-domino-muted',
};

/** U2: a value drawn as its format reads (date, figure, tags, mark, decision, link). */
export const CellValue = ({ display, tone, list = false, maxTags = 2 }: {
  display: CellDisplay;
  tone?: DataTableCellTone;
  /** In a list: one line, the whole value on hover. */
  list?: boolean;
  maxTags?: number;
}) => {
  const dot = tone ? <ToneDot tone={tone} className="mr-1.5 inline-block align-middle" /> : null;
  switch (display.kind) {
    case 'empty': return null;
    case 'number': return <span className="whitespace-nowrap tabular-nums">{dot}{display.text}</span>;
    case 'date': return <span className="whitespace-nowrap" title={display.exact}>{dot}{display.text}</span>;
    case 'tags': {
      const shown = list ? display.items.slice(0, maxTags) : display.items;
      const more = display.items.length - shown.length;
      return (
        <span className={cn('inline-flex gap-1', list ? 'flex-nowrap' : 'flex-wrap')} title={list ? display.items.join(', ') : undefined}>
          {dot}
          {shown.map((item) => <span key={item} className="whitespace-nowrap rounded-full border border-domino-cardBorder bg-domino-muted px-2 text-xs leading-5 text-domino-ink">{item}</span>)}
          {more > 0 && <span className="whitespace-nowrap text-xs leading-5 text-domino-inkSoft">+{more}</span>}
        </span>
      );
    }
    case 'rating': {
      const steps = Number.isInteger(display.max) && display.max <= 10 ? display.max : 0;
      return (
        <span className="inline-flex items-center gap-1.5 whitespace-nowrap tabular-nums">
          {dot}
          {steps > 0 && (
            <span aria-hidden="true" className="inline-flex gap-0.5">
              {Array.from({ length: steps }, (_, index) => (
                <span key={index} className={cn('h-1.5 w-3 rounded-full', index < Math.round(display.value) ? 'bg-domino-aubergine' : 'bg-domino-border')} />
              ))}
            </span>
          )}
          {display.text}
        </span>
      );
    }
    case 'decision':
      return (
        <span className={cn('inline-flex max-w-full items-center gap-1.5 rounded-full px-2 text-xs font-medium leading-5 text-domino-ink', TONE_PILL[tone ?? 'neutral'])}>
          {tone && <ToneDot tone={tone} />}
          <span className={list ? 'truncate' : undefined}>{display.text}</span>
        </span>
      );
    case 'link':
      return <a href={display.href} target="_blank" rel="noreferrer" onClick={(event) => event.stopPropagation()}
        className={cn('text-domino-aubergine underline decoration-domino-aubergine/30 underline-offset-2 hover:decoration-domino-aubergine', list ? 'truncate' : 'break-all')}>{display.text}</a>;
    case 'email':
      return <a href={display.href} onClick={(event) => event.stopPropagation()}
        className={cn('text-domino-aubergine underline decoration-domino-aubergine/30 underline-offset-2', list ? 'truncate' : 'break-all')}>{display.text}</a>;
    default:
      return list
        ? <span className="block truncate" title={display.text}>{dot}{display.text}</span>
        : <span className="whitespace-pre-wrap">{dot}{display.text}</span>;
  }
};

const BADGE_PALETTE = ['bg-domino-plumTint', 'bg-domino-lilac', 'bg-[#e8f5ec]', 'bg-domino-sand', 'bg-domino-muted'];
const badgeColor = (name: string) => BADGE_PALETTE[[...name].reduce((sum, char) => sum + char.charCodeAt(0), 0) % BADGE_PALETTE.length]!;

/** A company or a person of a list: its initials in a square, as a logo would sit. */
export const ObjectBadge = ({ name, size = 'sm' }: { name: string; size?: 'sm' | 'lg' }) => (
  <span aria-hidden="true" className={cn('inline-flex shrink-0 items-center justify-center font-semibold text-domino-ink',
    size === 'lg' ? 'h-11 w-11 rounded-xl text-sm' : 'h-7 w-7 rounded-lg text-[11px]', badgeColor(name))}>
    {initialsOf(name) || '?'}
  </span>
);
